import { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../../../contexts/authContext';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Loader } from '@mantine/core';

import NavbarApp from '../../../Navbar/NavbarApp';
import AppHdWithReturnLeft from '../../../../composents/Headers/AppHeaders/AppHdWithReturnLeft/AppHdWithReturnLeft';
import { IconChevronLeft } from '@tabler/icons-react';


const CONTRAT_LABELS = {
  Vente:    { label: "Vente",    color: "dark" },
  Location: { label: "Location", color: "indigo" },
  Don:      { label: "Don",      color: "teal" },
};

const FILTER_GROUPS = {
  all:       () => true,
  ongoing:   (tx) => ["funds_secured", "validated"].includes(tx.status),
  cancelled: (tx) => ["refunded", "cancelled_buyer", "cancelled_seller"].includes(tx.status),
  done:      (tx) => ["released", "to_released"].includes(tx.status),
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" }) : null;

const fmtAmount = (cents) =>
  cents != null ? (cents / 100).toFixed(2).replace(".", ",") + " €" : null;

const TransactionCard = ({ tx, isPurchaseTab, isMobile }) => {
  const contratInfo = CONTRAT_LABELS[tx.contrat] || { label: tx.contrat, color: "dark" };
  const navigate = useNavigate();

  const isOngoing = FILTER_GROUPS.ongoing(tx);
  const linkToConversation = isOngoing && !!tx.conversationId;
  const linkToOffer = isPurchaseTab && !isOngoing && !FILTER_GROUPS.cancelled(tx) && !!tx.offerId;
  const clickable = linkToConversation || linkToOffer;

  const handleClick = () => {
    if (linkToConversation) {
      navigate(isMobile ? `/app/msg/chat/${tx.conversationId}` : `/app/msg/list?conv=${tx.conversationId}`);
    }
    else if (linkToOffer) navigate(`/app/offers/offer/${tx.offerId}`);
  };

  return (
    <div
      className="conversation-item"
      style={{ cursor: clickable ? "pointer" : "default" }}
      onClick={clickable ? handleClick : undefined}
    >

      <div className="conversation-content" style={{ flex: 1 }}>

        <div className="conversation-header" style={{ gap: 6 }}>
          <div className="horizontal iCenter" style={{ gap: 6, minWidth: 0, flex: 1, overflow: "hidden" }}>
            <Badge size={isMobile ? "sm" : "lg"} color={contratInfo.color} variant="light" style={{ flexShrink: 0 }}>{contratInfo.label}</Badge>
            <span className="conversation-title medium" style={{ fontSize: isMobile ? 13 : 17, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.offerTitle}</span>
          </div>
          <span className="conversation-date" style={{ flexShrink: 0, marginLeft: 6, fontSize: isMobile ? undefined : 16 }}>{fmtDate(tx.createdAt)}</span>
        </div>

        <div className="conversation-preview" style={{ marginBottom: 4, fontSize: isMobile ? undefined : 16 }}>
          {tx.isSeller
            ? (tx.contrat === "Location" ? "Locataire : " : tx.contrat === "Don" ? "Bénéficiaire : " : "Acheteur : ")
            : (tx.contrat === "Location" ? "Bailleur : " : tx.contrat === "Don" ? "Donateur : " : "Vendeur : ")}
          {tx.otherParty || "—"}
        </div>

        {tx.contrat === "Location" && tx.startDate && tx.endDate && (
          <div className="conversation-preview" style={{ marginBottom: 4, fontSize: isMobile ? undefined : 16 }}>
            {fmtDate(tx.startDate)} — {fmtDate(tx.endDate)}
          </div>
        )}

        {!isPurchaseTab && tx.amount != null && tx.contrat !== "Don" && (
          <span className={isMobile ? "txt12 thin" : "thin"} style={isMobile ? undefined : { fontSize: 16 }}>{fmtAmount(tx.amount)}</span>
        )}

      </div>
    </div>
  );
};

const FilterButtons = ({ active, onChange, isMobile }) => (
  <div className="horizontal" style={{ gap: 6, flexWrap: "wrap" }}>
    {[
      { key: "done",      label: "Finalisées" },
      { key: "ongoing",   label: "En cours" },
      { key: "cancelled", label: "Annulées" },
    ].map(({ key, label }) => (
      <Button
        key={key}
        variant={active === key ? "filled" : "outline"}
        color={active === key ? "dark" : "gray"}
        size={isMobile ? "compact-sm" : "md"}
        radius="xl"
        onClick={() => onChange(key)}
      >
        {label}
      </Button>
    ))}
  </div>
);

const SalesAndPurchasesLists = () => {
  const { getToken } = useContext(AuthContext);
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("sales");
  const [filter, setFilter] = useState("ongoing");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef(null);

  useEffect(() => {
    const token = getToken();
    if (!token) { navigate("/app/login"); return; }

    const fetchTransactions = async () => {
      try {
        const res = await fetch(
          "https://boldee-97d8d36af3bd.herokuapp.com/app/payment/my-transactions",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        setTransactions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
  }, [loading]);

  // Reset filtre quand on change d'onglet
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFilter("ongoing");
  };

  const sales     = transactions.filter(tx => tx.isSeller && tx.contrat !== "Don");
  const purchases = transactions.filter(tx => !tx.isSeller && tx.contrat !== "Don");
  const dons      = transactions.filter(tx => tx.contrat === "Don");

  const CANCELLED_STATUSES = ["refunded", "cancelled_buyer", "cancelled_seller"];
  const sumAmount = (list) => list
    .filter(tx => !CANCELLED_STATUSES.includes(tx.status))
    .reduce((acc, tx) => acc + (tx.amount || 0), 0);
  const salesTotal = sumAmount(sales);
  const fmtTotal = (cents) => (cents / 100).toFixed(2).replace(".", ",") + " €";

  const filterFn = FILTER_GROUPS[filter] || (() => true);
  const displayedSales     = sales.filter(filterFn);
  const displayedPurchases = purchases.filter(filterFn);
  const displayedDons      = dons.filter(filterFn);

  if (loading) {
    return (
      <div className="loaderWrapper vertical">
        <Loader color="green" size="sm" type="dots" />
        <div className="mrgT5">Chargement ...</div>
      </div>
    );
  }

  const widthClass = isMobile ? "width90pCenter" : "width50pCenter";

  return (
    <div>
      <NavbarApp />

      {/* Bloc entièrement fixé en haut sur mobile */}
      <div ref={headerRef} style={isMobile ? {
        position: "fixed", top: 0, left: 0, right: 0,
        zIndex: 900, backgroundColor: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
      } : {}}>

        {/* Header Retour + titre */}
        {isMobile ? (
          <AppHdWithReturnLeft>Mes transactions</AppHdWithReturnLeft>
        ) : (
          <div className={widthClass} style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", margin: "40px auto 20px" }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                height: 36,
                width: 110,
                opacity: 0.9,
                backgroundColor: "#FFFFFF",
                borderRadius: 50,
                border: "none",
                padding: "0 12px",
                boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
                cursor: "pointer",
                justifySelf: "start",
              }}
            >
              <div className="horizontal iCenter">
                <IconChevronLeft width={20} />
                <p className="txt18 medium noMrgTB mrgL5">Retour</p>
              </div>
            </button>
            <h2 className="noMrgTB" style={{ gridColumn: 2, justifySelf: "center" }}>Mes transactions</h2>
            <div />
          </div>
        )}

        {/* Onglets */}
        <div className={`horizontal ${widthClass}`} style={{ borderBottom: "2px solid #dee2e6" }}>
          {[
            { key: "sales",     label: "Ventes/Locations",  sub: fmtTotal(salesTotal) },
            { key: "purchases", label: "Achats",  sub: null },
            { key: "dons",      label: "Dons",    sub: null },
          ].map(({ key, label, sub }) => (
            <button
              key={key}
              onClick={() => handleTabChange(key)}
              className="useRoboto"
              style={{
                flex: 1, padding: "10px 0", border: "none", background: "none", cursor: "pointer",
                fontWeight: activeTab === key ? "700" : "500",
                borderBottom: activeTab === key ? "2px solid #1c1c1c" : "2px solid transparent",
                marginBottom: "-2px", fontSize: isMobile ? 15 : 19, color: "#1c1c1c",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 1
              }}
            >
              <span>{label}</span>
              {sub && <span style={{ fontSize: isMobile ? 15 : 19, fontWeight: activeTab === key ? "700" : "500", color: "#1c1c1c" }}>{sub}</span>}
            </button>
          ))}
        </div>

        {/* Filtres */}
        <div className={`horizontal ${widthClass}`} style={{ padding: "8px 12px" }}>
          <FilterButtons active={filter} onChange={setFilter} isMobile={isMobile} />
        </div>
      </div>

      {/* Liste scrollable */}
      <div
        className={`conversations-container topBorder ${widthClass}`}
        style={{ paddingTop: isMobile ? headerHeight : 0 }}
      >
        {(() => {
          const list = activeTab === "sales" ? displayedSales : activeTab === "purchases" ? displayedPurchases : displayedDons;
          return list.length === 0
            ? <p className="txtCenter dGreyT mrgT50" style={{ fontSize: isMobile ? 14 : 17 }}>Aucune transaction</p>
            : list.map(tx => <TransactionCard key={tx._id} tx={tx} isPurchaseTab={activeTab === "purchases"} isMobile={isMobile} />);
        })()}
      </div>
    </div>
  );
};

export default SalesAndPurchasesLists;
